import { Box, Paper, Typography, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import type Employee from '@src/Objects/Employee';
import type { AppState } from '@store/store';
import React from 'react';
import { useSelector } from 'react-redux';

interface ContactWidgetProps {
    employee: Employee;
}

function ContactWidget({ employee }: ContactWidgetProps) {
    const theme = useTheme();
    const OUs = useSelector((state: AppState) => state.OUs);
    const OU = OUs?.find((o) => o.employees.includes(employee.id));

    return (
        <Paper
            elevation={0}
            sx={{
                p: { lg: 3, md: 2, xs: 2 },
                border: (theme) => `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                borderRadius: 2,
            }}
        >
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                Kontakt
            </Typography>

            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                    gap: 2,
                }}
            >
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {employee.email && (
                        <Box>
                            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                                E-Mail
                            </Typography>
                            <Box
                                component="a"
                                href={`mailto:${employee.email}`}
                                sx={{
                                    textDecoration: 'none',
                                    '&:hover': {
                                        textDecoration: 'underline',
                                    },
                                }}
                            >
                                <Typography
                                    variant="body2"
                                    sx={{
                                        fontWeight: 500,
                                        color: theme.palette.primary.main,
                                    }}
                                >
                                    {employee.email}
                                </Typography>
                            </Box>
                        </Box>
                    )}
                    <Box>
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                            Abteilung
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {OU?.title || 'Keine Abteilung zugeordnet'}
                        </Typography>
                    </Box>
                    <Box>
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                            Standort
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            Deutschland
                        </Typography>
                    </Box>
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {employee.linkedInUrl && (
                        <Box>
                            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                                LinkedIn
                            </Typography>
                            <Typography
                                variant="body2"
                                sx={{
                                    fontWeight: 500,
                                    color: theme.palette.primary.main,
                                    cursor: 'pointer',
                                }}
                                onClick={() => window.open(employee.linkedInUrl, '_blank')}
                            >
                                {employee.linkedInUrl}
                            </Typography>
                        </Box>
                    )}
                    {employee.slackUrl && (
                        <Box>
                            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                                Slack
                            </Typography>
                            <Typography
                                variant="body2"
                                sx={{
                                    fontWeight: 500,
                                    color: theme.palette.primary.main,
                                    cursor: 'pointer',
                                }}
                                onClick={() => window.open(employee.slackUrl, '_blank')}
                            >
                                {employee.slackUrl}
                            </Typography>
                        </Box>
                    )}
                    {employee.websiteUrl && (
                        <Box>
                            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                                Website
                            </Typography>
                            <Typography
                                variant="body2"
                                sx={{
                                    fontWeight: 500,
                                    color: theme.palette.primary.main,
                                    cursor: 'pointer',
                                }}
                                onClick={() => window.open(employee.websiteUrl, '_blank')}
                            >
                                {employee.websiteUrl}
                            </Typography>
                        </Box>
                    )}
                </Box>
            </Box>
        </Paper>
    );
}

export default ContactWidget;
